// Q3 onboarding hints: two short, fading hints shown once on the player's
// first prague session — "draw a line to a runway", then "tap a plane to
// hold it". Pure state machine driven by sim facts (world.elapsed, which
// aircraft need attention, how many are airborne) plus two explicit
// player-action notifications; never Date, never the render loop. main.ts
// calls `tick()` once per fixed sim step and applies the resulting
// `hint1Visible`/`hint2Visible` to the DOM only when they change.
import type { World } from '../sim/index.ts';
import { airborneCount, needsAttention } from '../sim/index.ts';

const STORAGE_KEY = 'qs.onboarded';

const HINT1_DELAY = 1.2;      // s of simulated time before hint1 may appear
const HINT1_TIMEOUT = 12;     // s hint1 stays up before it auto-fades
const HINT2_GAP_TIMEOUT = 8;  // s after hint1 resolves before hint2 shows regardless of traffic
const HINT2_TIMEOUT = 12;     // s hint2 stays up before it auto-fades

type Stage = 'waiting1' | 'hint1' | 'waiting2' | 'hint2' | 'done';

function alreadyOnboarded(): boolean {
  try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
}

function markOnboarded(): void {
  try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* Safari private mode — hints just replay next time */ }
}

export class Onboarding {
  private stage: Stage;
  private hint1ShownAt = 0;
  private hint1ResolvedAt = 0;
  private hint2ShownAt = 0;
  private committed = false;
  private held = false;

  constructor(levelId: string) {
    this.stage = levelId === 'prague' && !alreadyOnboarded() ? 'waiting1' : 'done';
  }

  get hint1Visible(): boolean { return this.stage === 'hint1'; }
  get hint2Visible(): boolean { return this.stage === 'hint2'; }

  /** The player released a genuine drawn path (any result: runway, pad or free). */
  notifyPathCommitted(): void { this.committed = true; }
  /** The player successfully put an aircraft into a holding pattern. */
  notifyHeld(): void { this.held = true; }

  /** Advance the state machine using the just-stepped world. Simulated time only. */
  tick(w: World): void {
    const t = w.elapsed;
    switch (this.stage) {
      case 'waiting1':
        if (this.committed) {
          // learned the draw gesture unaided — skip straight to hint2's window
          this.stage = 'waiting2';
          this.hint1ResolvedAt = t;
        } else if (t >= HINT1_DELAY && w.planes.some(needsAttention)) {
          this.stage = 'hint1';
          this.hint1ShownAt = t;
        }
        break;
      case 'hint1':
        if (this.committed || t - this.hint1ShownAt >= HINT1_TIMEOUT) {
          this.stage = 'waiting2';
          this.hint1ResolvedAt = t;
        }
        break;
      case 'waiting2':
        if (this.held) {
          this.finish();
        } else if (this.committed) {
          if (airborneCount(w) >= 2 || t - this.hint1ResolvedAt >= HINT2_GAP_TIMEOUT) {
            this.stage = 'hint2';
            this.hint2ShownAt = t;
          }
        }
        break;
      case 'hint2':
        if (this.held || t - this.hint2ShownAt >= HINT2_TIMEOUT) this.finish();
        break;
      default:
        break;
    }
  }

  private finish(): void {
    this.stage = 'done';
    markOnboarded();
  }
}
